import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { api, type Query } from './client'
import type { Page } from './types'

/** GET a paginated list. `params` is part of the query key, so changing a filter refetches. */
export function useList<T>(path: string, params?: Query, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [path, params ?? {}] as QueryKey,
    queryFn: ({ signal }) => api.get<Page<T>>(path, { query: params, signal }),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  })
}

/** GET one object (or a non-paginated array). Pass `null` to disable. */
export function useItem<T>(path: string | null, params?: Query) {
  return useQuery({
    queryKey: [path, params ?? {}] as QueryKey,
    queryFn: ({ signal }) => api.get<T>(path as string, { query: params, signal }),
    enabled: path !== null,
  })
}

type Method = 'post' | 'put' | 'patch'

/** A write that refreshes the lists/objects whose path starts with one of `invalidate`. */
export function useAction<Body, Result = unknown>(method: Method, path: string | ((body: Body) => string), invalidate: string[] = [], toBody?: (body: Body) => unknown) {
  const client = useQueryClient()
  return useMutation<Result, Error, Body>({
    mutationFn: (body) => api[method]<Result>(typeof path === 'function' ? path(body) : path, toBody ? toBody(body) : body),
    onSuccess: () => {
      for (const prefix of invalidate) {
        void client.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith(prefix) })
      }
    },
  })
}
